/**
 * Phase 5: Clinical sync status indicator – shows last DHIS2 sync for the current session.
 */
import { useState, useEffect } from 'react';
import { Chip, Tooltip, Typography } from '@mui/material';
import { CloudDone, CloudOff, CloudQueue } from '@mui/icons-material';
import { dhis2SyncAPI } from '../../services/api';

interface SyncStatusIndicatorProps {
  sessionId: string | null;
  compact?: boolean;
}

type Status = 'success' | 'failed' | 'none';

export default function SyncStatusIndicator({ sessionId, compact }: SyncStatusIndicatorProps) {
  const [status, setStatus] = useState<Status>('none');
  const [lastAt, setLastAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('none');
      setLastAt(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    dhis2SyncAPI
      .history({ session_id: sessionId, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const entries = res.data?.entries ?? [];
        if (entries.length === 0) {
          setStatus('none');
          setLastAt(null);
        } else {
          const e = entries[0];
          const ok = e.status_code != null && e.status_code >= 200 && e.status_code < 300;
          setStatus(ok ? 'success' : 'failed');
          setLastAt(e.created_at ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('none');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (!sessionId || loading) return null;

  const label =
    status === 'success'
      ? 'Synced'
      : status === 'failed'
        ? 'Sync failed'
        : 'Not synced';
  const icon =
    status === 'success' ? <CloudDone fontSize="small" /> : status === 'failed' ? <CloudOff fontSize="small" /> : <CloudQueue fontSize="small" />;
  const color = status === 'success' ? 'success' : status === 'failed' ? 'error' : 'default';

  const tooltip = lastAt ? `Last sync: ${new Date(lastAt).toLocaleString()}` : label;

  if (compact) {
    return (
      <Tooltip title={tooltip}>
        <Chip size="small" icon={icon} label={label} color={color} variant="outlined" />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip}>
      <Chip size="small" icon={icon} label={label} color={color} variant="outlined" sx={{ fontWeight: 500 }} />
    </Tooltip>
  );
}
